"use client"

import { useCallback, useRef } from "react"

/**
 * Keeps `result_state_shown.offered_actions` and every later
 * `result_next_action_clicked.position` pointing at the SAME array.
 *
 * The offered list is derived from render state, so it is recomputed on every
 * render, while the impression is delivered exactly once per mount
 * (see hooks/use-visible-impression.ts). Anything that changes the list after
 * that — a deferred picker revealing itself, a subscription lookup resolving,
 * a negative confidence answer revealing a second-look form — makes the click
 * index into a list the impression never reported. A position that indexes a
 * different array is worse than no position: it silently mis-attributes the
 * rank of an action inside its own denominator.
 *
 * The snapshot is taken lazily on first use — whichever of the impression or a
 * click happens first — and then frozen for the life of the mount, which is
 * also the life of the one impression. An action that only became available
 * after that is not in the frozen list, so `positionOf` returns `undefined`
 * and the click is emitted with no position at all. That is the honest answer:
 * the action was taken, and it had no rank in what was advertised.
 */
export function useOfferedActionsSnapshot<Action extends string>(
  offeredActions: readonly Action[],
) {
  const latestRef = useRef(offeredActions)
  latestRef.current = offeredActions
  const frozenRef = useRef<Action[] | null>(null)

  /** The offered list as of the first call. Report this, never the live array. */
  const snapshotOfferedActions = useCallback(() => {
    if (frozenRef.current === null) frozenRef.current = [...latestRef.current]
    return frozenRef.current
  }, [])

  /** Rank inside the reported list, or undefined for an action it never held. */
  const positionOf = useCallback((action: Action) => {
    const index = snapshotOfferedActions().indexOf(action)
    return index >= 0 ? index : undefined
  }, [snapshotOfferedActions])

  return { snapshotOfferedActions, positionOf }
}
